import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useCheckVersion } from '../../../../../hooks/useCheckVersion'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { useLookupDealerCollection } from '../../../../../hooks/useLookupedDealersCollection'
import { assignDealer } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../../utils/controllers/ModalController'
import { getComp } from '../../../../../utils/utils/DI'
import getNoun from '../../../../../utils/utils/getNoun'
import { adminRequestsCheckboxStore } from '../../../pages/AdminRequestsPage'
import { requestsPaginationStore } from '../../../RequestsSecondOperator/Table/hooks/useRequestsData'
import { newAssignDealerDrawerStore } from '../NewAssignDealerDrawerStore'

export const useNewAssignDealerDrawer = () => {
  const [dealer, setDealer] = useState('')
  const { isOpen, setIsOpen } = newAssignDealerDrawerStore
  const dealersStore = getComp('DealersStore')
  const { checkVersion } = useCheckVersion()
  const intl = useIntl()
  const invalidate = useInvalidateQueries()
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const { data, isLoading, getFittedDealers, dealerOrders, dealers } =
    useLookupDealerCollection(adminRequestsCheckboxStore.selectedIds, dealer)

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['group-assign-dealer'],
    mutationFn: () =>
      assignDealer({
        dealerId: dealer,
        orderIds: dealerOrders,
      }),
    onSuccess: (data) => {
      if (data) {
        const dealerName = dealersStore.getDealer(dealer).displayName
        closeModalFn['progress-backdrop']?.()

        const message = intl.formatMessage(
          {
            id: '{size} {sizeNoun} {sizeNounAction} на дилера {dealer}',
            defaultMessage: `{size} {sizeNoun} {sizeNounAction} на дилера {dealer}`,
          },
          {
            size: data.length,
            sizeNoun: getNoun(data.length, 'заявка', 'заявки', 'заявок'),
            sizeNounAction: getNoun(
              data.length,
              'назначена',
              'назначены',
              'назначены',
            ),
            dealer: dealerName,
          },
        )
        notifySuccess(message)

        if (data.length === adminRequestsCheckboxStore.selectedIds.length) {
          adminRequestsCheckboxStore.resetAll(true)
        } else {
          adminRequestsCheckboxStore.partialDeleteIds(
            data.map((el: any) => el.id),
          )
          adminRequestsCheckboxStore.setForbidResetSelectedIds(true)
        }
        invalidate(['requests-table'])
        setDealer('')
        setIsOpen(false)
        requestsPaginationStore.resetSelectedRow()
        timerRef.current = setTimeout(() => {
          adminRequestsCheckboxStore.setForbidResetSelectedIds(false)
        }, 100)
      }
    },

    onError: (error) => {
      closeModalFn['progress-backdrop']?.()

      return error
    },
  })

  const onAssignDealer = async () => {
    await checkVersion()
    openModalFn['transaction-confirmation']?.({
      text1: `Подтвердить назначение `,
      value1: `${dealerOrders.length}`,
      text2: getNoun(
        dealerOrders.length,
        `заявки на дилера ${dealersStore.getDealerName(dealer)}?`,
        `заявок на дилера ${dealersStore.getDealerName(dealer)}?`,
        `заявок на дилера ${dealersStore.getDealerName(dealer)}?`,
      ),
      action: mutateAsync,
      processText: `Выполняется назначение ${dealerOrders.length} ${getNoun(
        dealerOrders.length,
        'заявки',
        'заявок',
        'заявок',
      )} на дилера ${dealersStore.getDealerName(dealer)}`,
    })
  }

  useEffect(() => {
    if (isOpen) {
      getFittedDealers()
      setDealer('')
    }
  }, [isOpen])

  useEffect(() => {
    return () => {
      timerRef.current && clearTimeout(timerRef.current)
    }
  }, [])

  const onClose = () => setIsOpen(false)

  return {
    dealer,
    setDealer,
    onClose,
    dealers,
    onAssignDealer,
    isLoading,
    isPending,
    isOpen,
    data,
  }
}
